// צור קובץ חדש: components/dashboard/CombinedCommentsWidget.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, AlertCircle, CheckCircle, User, UserCheck, Calendar, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const CombinedCommentsWidget = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { comments, tasks, markCommentAsRead } = useData();
  const [activeTab, setActiveTab] = useState('others'); // 'others' or 'mine'
  
  
  // Get my tasks
  const myTasks = tasks.filter(task => 
    task.creatorId === currentUser.id || 
    task.assigneeId === currentUser.id ||
    (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser.id))
  );
  const myTaskIds = myTasks.map(task => task.id);
  
  console.log('My tasks:', myTasks.length);
  console.log('My task IDs:', myTaskIds);
  
  // Get unread comments from others
  const unreadCommentsFromOthers = comments.filter(comment => 
    myTaskIds.includes(comment.taskId) && 
    comment.userId !== currentUser.id &&
    !comment.isRead
  );
  
  console.log('Unread comments from others:', unreadCommentsFromOthers.length);
  console.log('Unread comments details:', unreadCommentsFromOthers);
  
  // Get my own comments on my tasks
  const myOwnComments = comments
    .filter(comment => 
      comment.userId === currentUser.id && 
      myTaskIds.includes(comment.taskId)
    )
    .sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    })
    .slice(0, 10); // Last 10 comments
  
  console.log('My own comments:', myOwnComments.length);
  
  // Group comments by task
  const groupCommentsByTask = (commentsList) => {
    return commentsList.reduce((acc, comment) => {
      if (!acc[comment.taskId]) {
        acc[comment.taskId] = [];
      }
      acc[comment.taskId].push(comment);
      return acc;
    }, {});
  };
  
  const handleViewTask = (taskId) => {
    navigate(`/tasks/${taskId}?tab=comments`);
  };
  
  const handleMarkAsRead = async (commentId, e) => {
    e.stopPropagation();
    await markCommentAsRead(commentId);
  };
  
  const renderCommentsList = (commentsList, showMarkAsRead = false) => {
    const commentsByTask = groupCommentsByTask(commentsList);
    
    if (Object.keys(commentsByTask).length === 0) {
      return (
        <div className="text-center py-8">
          <MessageCircle size={48} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {activeTab === 'others' ? 'אין הערות חדשות מאחרים' : 'לא הוספת הערות עדיין'}
          </p>
        </div>
      );
    }
    
    return (
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {Object.entries(commentsByTask).map(([taskId, taskComments]) => {
          const task = tasks.find(t => t.id === taskId);
          if (!task) return null;
          
          const isCreator = task.creatorId === currentUser.id;
          const isAssignee = task.assigneeId === currentUser.id || 
                           (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(currentUser.id));
          
          return (
            <div 
              key={taskId}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-all"
              onClick={() => handleViewTask(taskId)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-gray-900">
                      {task.title}
                    </h4>
                    {isCreator && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                        <User size={10} />
                        יצרת
                      </span>
                    )}
                    {isAssignee && !isCreator && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                        <UserCheck size={10} />
                        מוקצה אליך
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {taskComments.length} הערות
                  </p>
                </div>
                {activeTab === 'others' && (
                  <AlertCircle size={16} className="text-orange-500 animate-pulse" />
                )}
              </div>
              
              <div className="space-y-2">
                {taskComments.slice(0, 2).map(comment => (
                  <div 
                    key={comment.id} 
                    className={`rounded p-2 text-sm ${
                      activeTab === 'others' ? 'bg-orange-50' : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-xs ${
                          activeTab === 'others' ? 'text-orange-700' : 'text-blue-700'
                        }`}>
                          {comment.userName}
                        </span>
                        <span className="text-xs text-gray-500">
                          <Calendar size={10} className="inline ml-1" />
                          {comment.createdAt?.toDate ? 
                            new Date(comment.createdAt.toDate()).toLocaleDateString('he-IL') :
                            new Date(comment.createdAt).toLocaleDateString('he-IL')}
                        </span>
                      </div>
                      {showMarkAsRead && (
                        <button
                          onClick={(e) => handleMarkAsRead(comment.id, e)}
                          className="text-xs text-orange-600 hover:text-orange-800 underline"
                        >
                          סמן כנקרא
                        </button>
                      )}
                    </div>
                    <p className="text-gray-700 line-clamp-2">{comment.content}</p>
                  </div>
                ))}
                {taskComments.length > 2 && (
                  <p className="text-xs text-gray-500 text-center">
                    ועוד {taskComments.length - 2} הערות...
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            unreadCommentsFromOthers.length > 0 
              ? 'bg-orange-100 animate-pulse' 
              : 'bg-gray-100'
          }`}>
            <MessageSquare size={20} className={
              unreadCommentsFromOthers.length > 0 
                ? 'text-orange-600' 
                : 'text-gray-600'
            } />
          </div>
          <h3 className="text-lg font-semibold">הערות במשימות</h3>
          {unreadCommentsFromOthers.length > 0 && activeTab === 'others' && (
            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
              {unreadCommentsFromOthers.length} חדשות
            </span>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setActiveTab('others')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'others'
              ? 'text-orange-600 border-orange-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          הערות מאחרים
          {unreadCommentsFromOthers.length > 0 && (
            <span className="mr-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
              {unreadCommentsFromOthers.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('mine')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'mine'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          ההערות שלי
          {myOwnComments.length > 0 && (
            <span className="mr-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
              {myOwnComments.length}
            </span>
          )}
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'others' 
        ? renderCommentsList(unreadCommentsFromOthers, true)
        : renderCommentsList(myOwnComments, false)
      }
      
      <div className="mt-4 pt-4 border-t">
        <button
          onClick={() => navigate('/tasks')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          צפה בכל המשימות ←
        </button>
      </div>
    </div>
  );
};

export default CombinedCommentsWidget;